import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

import type { ChannelSubscriptionLevel } from '../../types/ChannelSubscriptionLevel';
import { ChannelVisibility } from '../../types/ChannelVisibility';
import type { CommunityChannelMember } from '../../types/CommunityChannelMember';
import { CommunityChannelMemberStatus } from '../../types/CommunityChannelMemberStatus';
import { memberHasChannelSubscriptionAccess } from './memberHasChannelSubscriptionAccess';

/**
 * Whether a viewer may READ a community channel — the web port of PopshopRN's
 * `WithChannelAccessGuard` + `useJoinCommunityChannel` read gate:
 *
 *   - the creator (owner) sees every channel
 *   - a **public** channel is open to everyone (the subscription level only
 *     gates *joining* / posting, not reading — handled by the join flow)
 *   - a **private** channel requires an **active membership** AND a satisfying
 *     subscription level (current, or grandfathered against the member's join-time
 *     snapshot — see {@link memberHasChannelSubscriptionAccess})
 *
 * Being merely a member is NOT enough: the backend gates the private feed on the
 * current subscription level, so a member whose subscription fell below the bar
 * gets an empty feed — this gate mirrors that so they see the join/subscribe
 * blocker instead of an empty channel.
 *
 * Pure — the viewer's standing + membership are resolved by the caller.
 */
export function canViewChannel(params: {
  visibility: ChannelVisibility;
  minimumSubscriptionLevel: ChannelSubscriptionLevel;
  isOwner: boolean;
  member: CommunityChannelMember | null;
  viewerLevel: PopStoreExtendedSubscriptionLevel | undefined;
}): boolean {
  const { visibility, minimumSubscriptionLevel, isOwner, member, viewerLevel } =
    params;

  if (isOwner) return true;
  if (visibility === ChannelVisibility.Public) return true;
  if (member?.status !== CommunityChannelMemberStatus.Active) return false;
  return memberHasChannelSubscriptionAccess({
    viewerLevel,
    channelMinimumSubscriptionLevel: minimumSubscriptionLevel,
    member,
  });
}
